"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawerCanvas = void 0;
const color_1 = require("@urpflanze/color");
const Utilities_1 = require("@urpflanze/core/dist/cjs/Utilities");
const math_1 = require("@urpflanze/core/dist/cjs/math");
const Vec2_1 = require("@urpflanze/core/dist/cjs/math/Vec2");
const canvas_1 = require("canvas");
const Emitter_1 = require("./Emitter");
const Timeline_1 = require("./Timeline");
const utils_1 = require("./utils");
const bSupportOffscreen = utils_1.bBrowser &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen !== 'undefined';
/**
 *
 * @category DrawerCanvas
 * @extends {Emitter<DrawerCanvasEvents>}
 */
class DrawerCanvas extends Emitter_1.Emitter {
    constructor(scene, canvasOrContainer, drawerOptions, duration = 60000, framerate = 60, tickMode = (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.clear) === false ? 'linear' : 'async') {
        var _a, _b, _c, _d, _e;
        super();
        this.drawerOptions = {
            width: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.width) || (scene === null || scene === void 0 ? void 0 : scene.width) || 400,
            height: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.height) || (scene === null || scene === void 0 ? void 0 : scene.height) || 400,
            clear: (_a = drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.clear) !== null && _a !== void 0 ? _a : true,
            time: (_b = drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.time) !== null && _b !== void 0 ? _b : 0,
            simmetricLines: (_c = drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.simmetricLines) !== null && _c !== void 0 ? _c : 0,
            noBackground: (_d = drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.noBackground) !== null && _d !== void 0 ? _d : false,
            ghosts: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.ghosts) || 0,
            ghostAlpha: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.ghostAlpha) === false ? false : true,
            ghostSkipTime: (_e = drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.ghostSkipTime) !== null && _e !== void 0 ? _e : 30,
            ghostSkipFunction: drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.ghostSkipFunction,
            sceneFit: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.sceneFit) || 'contain',
            backgroundImage: drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.backgroundImage,
            backgroundImageFit: (drawerOptions === null || drawerOptions === void 0 ? void 0 : drawerOptions.backgroundImageFit) || 'cover',
        };
        this.timeline = new Timeline_1.Timeline(duration, framerate, tickMode);
        this.timeline.setTime(this.drawerOptions.time || 0);
        this.draw_id = null;
        this.redraw_id = null;
        this.animation_id = null;
        if (scene) {
            this.setScene(scene);
        }
        if (!utils_1.bWorker || (utils_1.bWorker && bSupportOffscreen && canvasOrContainer instanceof OffscreenCanvas))
            this.setCanvas(canvasOrContainer);
    }
    /**
     * Return option value or default
     *
     * @param {K keyof IDrawerCanvasOptions} name
     * @param {IDrawerCanvasOptions[K]} defaultValue
     */
    getOption(name, defaultValue) {
        var _a;
        return (_a = this.drawerOptions[name]) !== null && _a !== void 0 ? _a : defaultValue;
    }
    /**
     * Set Drawer options
     *
     * @param {K keyof TDrawerOptions} name
     * @param {TDrawerOptions[K]} value
     */
    setOption(name, value) {
        this.drawerOptions[name] = value;
    }
    /**
     * Create instance of canvas (HTMLCanvasElement in browser o Canvas in Node)
     */
    setCanvas(canvasOrContainer) {
        if (utils_1.bWorker) {
            if (bSupportOffscreen && canvasOrContainer instanceof OffscreenCanvas) {
                this.canvas = canvasOrContainer;
            }
            else {
                console.error('Cannot set cavas');
            }
        }
        else {
            if (utils_1.bBrowser) {
                const canvas = canvas_1.createCanvas(this.drawerOptions.width, this.drawerOptions.height);
                if (canvasOrContainer instanceof HTMLElement &&
                    !(canvasOrContainer instanceof HTMLCanvasElement ||
                        (bSupportOffscreen && canvasOrContainer instanceof OffscreenCanvas))) {
                    this.canvas = canvas;
                    while (canvasOrContainer.lastChild)
                        canvasOrContainer.removeChild(canvasOrContainer.lastChild);
                    canvasOrContainer.appendChild(canvas);
                }
                else {
                    this.canvas = typeof canvasOrContainer === 'undefined' ? canvas : canvasOrContainer;
                }
            }
            else {
                this.canvas = canvas_1.createCanvas(this.drawerOptions.width, this.drawerOptions.height);
            }
        }
        if (this.canvas) {
            this.canvas.width = this.drawerOptions.width;
            this.canvas.height = this.drawerOptions.height;
            this.context = this.canvas.getContext('2d', {
                alpha: true,
                // desynchronized: true,
            });
        }
    }
    /**
     * Return instance of canvas
     *
     * @returns canvas
     */
    getCanvas() {
        return this.canvas;
    }
    setScene(scene) {
        this.scene = scene;
    }
    draw() {
        if (this.context === null || typeof this.scene === 'undefined')
            return 0;
        const start_time = Utilities_1.now();
        const timeline = this.timeline;
        const drawAtTime = timeline.getTime();
        const currentFrame = timeline.getFrameAtTime(drawAtTime);
        const drawerOptions = {
            ...this.drawerOptions,
            ghostIndex: undefined,
            clear: this.drawerOptions.clear || currentFrame <= 0,
            time: drawAtTime,
        };
        this.dispatch('drawer-canvas:before_draw', {
            currentFrame: currentFrame,
            currentTime: drawAtTime,
        });
        if (drawerOptions.simmetricLines > 0) {
            if (drawerOptions.clear) {
                DrawerCanvas.clear(this.context, drawerOptions.width, drawerOptions.height, drawerOptions.noBackground ? false : this.scene.background, drawerOptions.backgroundImage, drawerOptions.backgroundImageFit);
            }
            DrawerCanvas.drawSimmetricLines(this.context, drawerOptions.simmetricLines, drawerOptions.width, drawerOptions.height, this.scene.color);
            drawerOptions.clear = false;
        }
        if (drawerOptions.ghosts) {
            const ghostDrawerOptions = {
                ...drawerOptions,
            };
            const drawAtTime = timeline.getTime();
            const sequenceDuration = timeline.getDuration();
            const ghostRepetition = {
                offset: 0,
                index: 0,
                count: drawerOptions.ghosts,
            };
            for (let i = 1; i <= drawerOptions.ghosts; i++) {
                ghostRepetition.index = i;
                ghostRepetition.offset = ghostRepetition.index / ghostRepetition.count;
                const ghostTime = drawAtTime -
                    (drawerOptions.ghostSkipFunction
                        ? drawerOptions.ghostSkipFunction(ghostRepetition, drawAtTime)
                        : i * drawerOptions.ghostSkipTime);
                ghostDrawerOptions.ghostIndex = i;
                ghostDrawerOptions.time = math_1.mod(ghostTime, sequenceDuration);
                ghostDrawerOptions.clear = drawerOptions.clear && ghostDrawerOptions.ghostIndex === 1;
                this.realDraw(ghostDrawerOptions);
            }
            drawerOptions.clear = false;
        }
        this.realDraw(drawerOptions);
        return Utilities_1.now() - start_time;
    }
    realDraw(options) {
        var _a;
        const width = this.drawerOptions.width;
        const height = this.drawerOptions.height;
        const context = this.context;
        const scene = this.scene;
        const time = (_a = options.time) !== null && _a !== void 0 ? _a : 0;
        const bGhost = typeof options.ghosts !== 'undefined' &&
            options.ghosts > 0 &&
            typeof options.ghostIndex !== 'undefined' &&
            options.ghostIndex > 0;
        const ghostMultiplier = bGhost ? 1 - options.ghostIndex / (options.ghosts + 0.5) : 0;
        const ghostAlpha = options.ghostAlpha === true;
        const sceneFit = utils_1.fit(scene.width, scene.height, width, height, this.drawerOptions.sceneFit);
        const translateX = sceneFit.x;
        const translateY = sceneFit.y;
        options.clear &&
            DrawerCanvas.clear(context, width, height, options.noBackground ? false : scene.background, options.backgroundImage, options.backgroundImageFit);
        let logFillColorWarn = false;
        let logStrokeColorWarn = false;
        scene.currentTime = time;
        const sceneChilds = scene.getChildren();
        for (let i = 0, len = sceneChilds.length; i < len; i++) {
            const sceneChild = sceneChilds[i];
            if (!sceneChild.data ||
                (!(sceneChild.data.visible === false) && !(bGhost && sceneChild.data.disableGhost === true))) {
                sceneChilds[i].generate(time, true);
                const childIndexedBuffer = sceneChilds[i].getIndexedBuffer() || [];
                const childBuffer = sceneChilds[i].getBuffer() || [];
                let childVertexIndex = 0;
                for (let currentBufferIndex = 0, len = childIndexedBuffer.length; currentBufferIndex < len; currentBufferIndex++) {
                    const currentIndexing = childIndexedBuffer[currentBufferIndex];
                    const shape = currentIndexing.shape;
                    const propArguments = {
                        canvasContext: context,
                        ...currentIndexing,
                    };
                    const composite = DrawerCanvas.getStreamDrawerProp(shape, 'composite', propArguments, 'source-over');
                    context.globalCompositeOperation = composite;
                    context.beginPath();
                    context.moveTo(childBuffer[childVertexIndex] * sceneFit.scale + translateX, childBuffer[childVertexIndex + 1] * sceneFit.scale + translateY);
                    childVertexIndex += 2;
                    for (let currentFrameLength = childVertexIndex + currentIndexing.frameLength - 2; childVertexIndex < currentFrameLength; childVertexIndex += 2)
                        context.lineTo(childBuffer[childVertexIndex] * sceneFit.scale + translateX, childBuffer[childVertexIndex + 1] * sceneFit.scale + translateY);
                    if (shape.isClosed())
                        context.closePath();
                    const alpha = DrawerCanvas.getStreamDrawerProp(shape, 'opacity', propArguments, 1);
                    context.globalAlpha = alpha;
                    const shadowColor = DrawerCanvas.getStreamDrawerProp(shape, 'shadowColor', propArguments);
                    const shadowBlur = DrawerCanvas.getStreamDrawerProp(shape, 'shadowBlur', propArguments);
                    const shadowOffsetX = DrawerCanvas.getStreamDrawerProp(shape, 'shadowOffsetX', propArguments);
                    const shadowOffsetY = DrawerCanvas.getStreamDrawerProp(shape, 'shadowOffsetY', propArguments);
                    context.shadowColor = shadowColor;
                    context.shadowBlur = shadowBlur;
                    shadowOffsetX && (context.shadowOffsetX = shadowOffsetX);
                    shadowOffsetY && (context.shadowOffsetY = shadowOffsetY);
                    let fill = DrawerCanvas.getStreamDrawerProp(shape, 'fill', propArguments);
                    if (typeof fill !== 'undefined') {
                        if (bGhost && ghostAlpha) {
                            const color = DrawerCanvas.ghostifyColor(fill, ghostMultiplier);
                            if (color) {
                                fill = color;
                            }
                            else if (!logFillColorWarn) {
                                console.warn(`[Urpflanze:DrawerCanvas] Unable ghost fill color '${fill}',
								please enter a rgba or hsla color`);
                                logFillColorWarn = true;
                            }
                        }
                        context.fillStyle = fill;
                        context.fill();
                    }
                    let stroke = DrawerCanvas.getStreamDrawerProp(shape, 'stroke', propArguments, typeof fill === 'undefined' ? scene.color : undefined);
                    let lineWidth = DrawerCanvas.getStreamDrawerProp(shape, 'lineWidth', propArguments, 1);
                    if (stroke) {
                        if (bGhost && ghostAlpha) {
                            const color = DrawerCanvas.ghostifyColor(stroke, ghostMultiplier);
                            if (color) {
                                stroke = color;
                            }
                            else if (!logStrokeColorWarn) {
                                console.warn(`[Urpflanze:DrawerCanvas] Unable ghost stroke color '${stroke}',
								please enter a rgba or hsla color`);
                                logStrokeColorWarn = true;
                            }
                            lineWidth *= ghostMultiplier;
                        }
                        const lineJoin = DrawerCanvas.getStreamDrawerProp(shape, 'lineJoin', propArguments);
                        const lineCap = DrawerCanvas.getStreamDrawerProp(shape, 'lineCap', propArguments);
                        const lineDash = DrawerCanvas.getStreamDrawerProp(shape, 'lineDash', propArguments);
                        const lineDashOffset = DrawerCanvas.getStreamDrawerProp(shape, 'lineDashOffset', propArguments);
                        const miterLimit = DrawerCanvas.getStreamDrawerProp(shape, 'miterLimit', propArguments);
                        context.setLineDash.call(context, lineDash || []);
                        context.lineJoin = lineJoin;
                        context.lineCap = lineCap;
                        context.lineDashOffset = lineDashOffset;
                        context.miterLimit = miterLimit;
                        context.lineWidth = lineWidth * sceneFit.scale;
                        context.strokeStyle = stroke;
                        context.stroke();
                    }
                }
                context.restore();
            }
        }
    }
    /**
     * Return a drawer value
     *
     * @static
     * @template T
     * @param {ShapePrimitive<T>} shape
     * @param {keyof T} key
     * @param {IDrawerPropArguments} propArguments
     * @param {*} [defaultValue]
     * @returns {*}
     */
    static getStreamDrawerProp(shape, key, propArguments, defaultValue) {
        let attribute = shape.drawer[key];
        if (typeof attribute === 'function') {
            attribute = attribute(propArguments);
        }
        return attribute !== null && attribute !== void 0 ? attribute : defaultValue;
    }
    /**
     * Create color based on ghostMultiplier
     *
     * @static
     * @param {any} color
     * @param {number} ghostMultiplier
     * @return {*}  {(string | undefined)}
     */
    static ghostifyColor(color, ghostMultiplier) {
        if (typeof color === 'string' || typeof color === 'number') {
            const parsed = color_1.parseColor(color);
            if (parsed) {
                const ghostAlpha = parsed.alpha * ghostMultiplier;
                return parsed.type === 'rgb'
                    ? `rgba(${parsed.a},${parsed.b},${parsed.c},${ghostAlpha})`
                    : `hsla(${parsed.a},${parsed.b}%,${parsed.c}%,${ghostAlpha})`;
            }
        }
        return color;
    }
    /**
     * Clear canvas, draw background or image (and fit)
     *
     * @param context
     * @param width
     * @param height
     * @param background
     * @param backgroundImage
     * @param backgroundImageFit
     */
    static clear(context, width, height, background, backgroundImage, backgroundImageFit) {
        if (typeof background === 'boolean' && background === false) {
            context.clearRect(0, 0, width, height);
        }
        else {
            context.globalCompositeOperation = 'source-over';
            context.fillStyle = background; // or true
            context.fillRect(0, 0, width, height);
            if (backgroundImage) {
                const sourceWidth = backgroundImage instanceof SVGImageElement ? backgroundImage.width.baseVal.value : backgroundImage.width;
                const sourceHeight = backgroundImage instanceof SVGImageElement ? backgroundImage.height.baseVal.value : backgroundImage.height;
                const fitRect = utils_1.fit(sourceWidth, sourceHeight, width, height, backgroundImageFit);
                context.drawImage(backgroundImage, fitRect.x, fitRect.y, fitRect.width, fitRect.height);
            }
        }
    }
    /**
     * Draw utility lines
     *
     * @param context
     * @param simmetricLines
     * @param width
     * @param height
     * @param color
     */
    static drawSimmetricLines(context, simmetricLines, width, height, color) {
        const offset = Math.PI / simmetricLines;
        const size = Math.max(width, height);
        const sizeMin = Math.min(width, height);
        const k = width < height ? 1 : 0;
        const centerX = [size / 2, size / 2];
        const centerY = [sizeMin / 2, sizeMin / 2];
        for (let i = 0; i < simmetricLines; i++) {
            const a = [-size, -size];
            const b = [size * 2, size * 2];
            const rotate = i * offset + Math.PI / 4;
            Vec2_1.default.rotateZ(a, i % 2 === k ? centerX : centerY, rotate);
            Vec2_1.default.rotateZ(b, i % 2 === k ? centerX : centerY, rotate);
            context.beginPath();
            context.strokeStyle = color;
            context.lineWidth = 1;
            context.moveTo(a[0], a[1]);
            context.lineTo(b[0], b[1]);
            context.stroke();
        }
    }
}
exports.DrawerCanvas = DrawerCanvas;
//# sourceMappingURL=DrawerCanvas.js.map